import { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, Wrench, Loader2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Super-admin only widget. Scans the database for documents missing a
 * `tenant_id` field and lets the admin remediate orphans with one click.
 * Renders nothing for non-super-admin users.
 */
export default function TenantAuditWidget() {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin' || user?.role === 'admin';

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/tenant-audit');
      setReport(data);
    } catch (e) {
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuper) scan();
  }, [isSuper]);

  if (!isSuper) return null;

  const orphans = report?.orphans_total || 0;
  const healthy = !loading && orphans === 0;
  const problem = !loading && orphans > 0;

  const fixNow = async () => {
    setFixing(true);
    try {
      const { data } = await api.post('/admin/tenant-audit/assign-orphans');
      toast.success(`Reassigned ${data?.total ?? 0} orphan document${(data?.total ?? 0) === 1 ? '' : 's'} to the default tenant.`);
      await scan();
    } catch (e) {
      toast.error('Remediation failed. Check backend logs.');
    } finally {
      setFixing(false);
    }
  };

  const offenders = report
    ? Object.entries(report.collections || {})
        .filter(([, v]) => (v?.orphaned || 0) > 0)
        .sort((a, b) => b[1].orphaned - a[1].orphaned)
    : [];

  return (
    <div
      data-testid="tenant-audit-widget"
      className={`relative overflow-hidden rounded-2xl border p-5 shadow-[0_4px_24px_-8px_rgba(0,0,0,0.08)] animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out ${
        problem
          ? 'bg-rose-50/70 border-rose-200'
          : 'bg-emerald-50/70 border-emerald-200'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
              problem ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-600'
            }`}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : problem ? (
              <ShieldAlert className="h-5 w-5" />
            ) : (
              <ShieldCheck className="h-5 w-5" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Outfit' }}>
                Multi-Tenant Isolation Audit
              </h3>
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                  problem
                    ? 'bg-rose-100 text-rose-700 border-rose-200'
                    : 'bg-emerald-100 text-emerald-700 border-emerald-200'
                }`}
                data-testid="tenant-audit-status-badge"
              >
                {loading ? 'Scanning…' : healthy ? 'Healthy' : 'Action Required'}
              </span>
            </div>
            <p className="text-xs text-slate-600 mt-1">
              {loading
                ? 'Checking every tenant-scoped collection…'
                : healthy
                ? 'Every document is correctly scoped to a tenant. No data-leak risk detected.'
                : `${orphans} document${orphans === 1 ? '' : 's'} missing a tenant_id — they are currently invisible to branch filtering.`}
            </p>

            {problem && offenders.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="tenant-audit-offenders">
                {offenders.map(([col, v]) => (
                  <li
                    key={col}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-white/80 border border-rose-200 text-rose-700 font-mono"
                  >
                    {col} · {v.orphaned}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={scan}
            disabled={loading || fixing}
            className="h-8 rounded-full gap-1 text-xs"
            data-testid="tenant-audit-rescan-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Re-scan
          </Button>
          {problem && (
            <Button
              size="sm"
              onClick={fixNow}
              disabled={fixing || loading}
              className="h-8 rounded-full gap-1 text-xs bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="tenant-audit-fix-btn"
            >
              {fixing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wrench className="h-3.5 w-3.5" />}
              Fix Now
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
